/**
 * A CHOICE TILE'S NAME IS AT MOST TWO LINES OF WHOLE WORDS (Swe Sosetta run 6, round D: S1, 2026-10-28).
 *
 * On a 390 px phone the design tiles read «أبيض بتق…» and «أبيض وخو…»: the tile was as wide as its 52 px picture and
 * its name one line ending in «…», so the words that tell two designs apart were the ones cut. The ruling: a tile's
 * label wraps to at most 2 lines, is never cut mid-word, and the tile grows to fit.
 *
 * This picks the one place a name may break. The tile draws each line as an unbroken box (`.colorThumbLine`,
 * `white-space: nowrap`), so the name can only wrap at this one gap: never a third line, never inside a word. The
 * tile is as wide as its longer line (`width: min-content`), so a name that fits on one line stays on one line, and
 * one that does not breaks here.
 *
 * The break evens the two lines, counted in letters: a mark (Arabic harakat and shadda, Hebrew niqqud) takes no width
 * of its own, so it is not counted. On a tie the earlier break wins, which keeps a name's first word on top
 * («أبيض» / «بتقطير وردي»). Runs of spaces are one gap; a name of one word is one line, however long.
 */
const MARKS = /\p{M}/gu;

/** How long `text` reads: its characters, marks left out. */
const lettersOf = (text) => Array.from(text.replace(MARKS, '')).length;

/**
 * The tile's lines for `label`: one line for one word, else two lines of whole words, as even as the words allow.
 * `lines.join(' ')` is the label with its spaces made single.
 */
export function tileLabelLines(label) {
  const words = String(label ?? '').trim().split(/\s+/).filter(Boolean);
  if (words.length < 2) return [words[0] || ''];
  let best = null;
  for (let at = 1; at < words.length; at += 1) {
    const first = words.slice(0, at).join(' ');
    const second = words.slice(at).join(' ');
    const longer = Math.max(lettersOf(first), lettersOf(second));
    if (!best || longer < best.longer) best = { longer, lines: [first, second] };
  }
  return best.lines;
}

/**
 * The hidden semibold copy the tile reserves its width and height with (`data-reserve`, drawn by
 * `.colorThumbName::after`): the same lines, each line's own spaces unbreakable, so the copy can break only where
 * the name does. A chosen tile's name turns semibold; because the copy already is, choosing moves nothing.
 */
export const tileLabelReserve = (lines) => lines.map((line) => line.replace(/ /g, ' ')).join(' ');
